import { Box, Button } from '@mui/material';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { useGet } from 'hooks/use-get';
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useReactToPrint } from 'react-to-print';
import { downloadOrderDocuments } from 'views/delivery-board/api/delivery-board';
import { getOrderHistoryDeliveryDocumentation } from 'views/order-feed/features/order-history/api/get-order-history-delivery-documentation';
import { OrderAttachment } from 'views/order-feed/types/order-attachment.type';
import FlexBox from './flex-box';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const maxWidth = 800;

const printContainerStyles = {
  width: '100%',
  maxWidth: 'calc(100% - 2em)',
  margin: '1em 0',
  '@media print': {
    margin: 0,
    padding: 0,
    '& .pdf-page': {
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
    },
    '& canvas': {
      pageBreakInside: 'avoid !important',
      breakInside: 'avoid !important',
    },
  },
};

type PDFFile = string | File | null;

type Props = {
  orderAttachment: OrderAttachment;
  from: 'delivery-board' | 'order-history';
};

const formatFileName = (fileName: string) => fileName.replace(/^[a-zA-Z0-9](?:[a-zA-Z0-9 ._-]*[a-zA-Z0-9])?\.[a-zA-Z0-9_-]+$/, '');

const PdfViewer: React.FC<Props> = ({ orderAttachment, from }) => {
  const intl = useIntl();
  const printRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef: printRef });
  const snackbarDispatch = useSnackbarDispatch();
  const { account } = useAccountState();

  const [file, setFile] = useState<Blob | undefined>();
  const [fileName, setFileName] = useState<string>('');
  const [documentId, setDocumentId] = useState<string>('');

  const [numPages, setNumPages] = useState<number>();

  const documentIds = useMemo(() => orderAttachment.documentIds, [orderAttachment]);

  // Users have different authorizations, so we need to use a different endpoint for delivery board or order history. Delivery Board uses customer number while
  const { isLoading, data, refetch } = useGet(
    [
      'downloadOrderDocuments',
      from === 'delivery-board' ? account.company?.customerErpId : account.company?.companyId,
      orderAttachment.orderNumber,
      documentId,
    ],
    ({ queryKey }) =>
      from === 'delivery-board'
        ? downloadOrderDocuments(String(queryKey[1]), String(queryKey[2]), String(queryKey[3]))
        : getOrderHistoryDeliveryDocumentation(String(queryKey[1]), String(queryKey[2]), String(queryKey[3])),
    {
      enabled: !!orderAttachment.orderNumber && !!documentId,
    }
  );

  useEffect(() => {
    if (data) setFile(data);
  }, [data]);

  useEffect(() => {
    if (!!documentIds) {
      setDocumentId(!!documentIds ? documentIds[0] : '');
      setFileName(
        formatFileName(
          `${orderAttachment.siteName} (#${orderAttachment.orderNumber}) - ${dayjs(orderAttachment.dispatchDate).format('LL')} ${Number(documentIds.length) > 1 ? `- ${1}` : ''}` // 'Site Name #123 - 2024-11-07 (1)'
        )
      );
    }
  }, [documentIds]);

  const handleDownload = async (documentBlob: Blob) => {
    try {
      const url = window.URL.createObjectURL(documentBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      snackbarDispatch({
        type: SnackbarActionTypes.WARNING_SNACKBAR,
        payload: { msg: error ? String(error) : intl.formatMessage({ id: 'snackbar.errors.default' }) },
      });
    }
  };

  const options = useMemo(
    () => ({
      cMapUrl: '/cmaps/',
      standardFontDataUrl: '/standard_fonts/',
    }),
    []
  );

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }: PDFDocumentProxy): void => {
    setNumPages(nextNumPages);
  };

  const selectedFile = useMemo(() => file, [file]);

  const handleFileIdSelection = (documentId: string, fileName: string) => {
    setDocumentId(documentId);
    setFileName(fileName);
    refetch();
  };

  return (
    <FlexBox display={'flex'} justifyContent={'center'} flexDirection={'column'}>
      <Box>
        <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
          <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'} gap={2}>
            {!!documentIds?.length &&
              documentIds.map((documentId, idx) => {
                const fileName = formatFileName(
                  `#${orderAttachment.orderNumber} - ${dayjs(orderAttachment.dispatchDate).format('LL')} ${Number(documentIds.length) > 1 ? `- ${idx + 1}` : ''}`
                );

                return (
                  <Button
                    key={idx}
                    variant="outlined"
                    onClick={() => {
                      handleFileIdSelection(documentId, fileName);
                    }}>
                    {fileName}
                  </Button>
                );
              })}
          </Box>
          <Box display={'flex'} flexDirection={'row'} gap={4}>
            <Box>
              <Button
                size='medium'
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
                onClick={() => handleDownload(file as Blob)}>
                <FormattedMessage id={`general.download`} />
              </Button>
            </Box>
            <Box>
              <Button
                size='medium'
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
                onClick={() => reactToPrintFn()}>
                <FormattedMessage id={`general.print`} />
              </Button>
            </Box>
          </Box>

        </Box>
        <style>
          {`
            @media print {
              #print-container {
                margin: 0;
                padding: 0;
              }
              .pdf-page {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              canvas {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
            }
          `}
        </style>

        <Box sx={printContainerStyles} id="print-container" ref={printRef}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <Document
              file={selectedFile}
              onLoadSuccess={onDocumentLoadSuccess}
              noData={'Select file from the list'}
              options={options}
              loading={<LoadingSpinner />}>
              {Array.from(new Array(numPages), (_el, index) => (
                <Page key={`page_${index + 1}`} pageNumber={index + 1} width={maxWidth} className="pdf-page" />
              ))}
            </Document>
          )}
        </Box>
      </Box>
    </FlexBox>
  );
};

export default PdfViewer;
